import { useState } from 'react'
import {Mail, MapPin, Clock, MessageCircleDashed as MessageCircle, Send, Check} from 'lucide-react'
import SectionHeading from '@/components/SectionHeading'

const regions = [
  { name: 'West', office: 'Vancouver HQ', email: 'info@deltafacilitiesservices.com' },
  { name: 'Prairies', office: 'Calgary Office', email: 'info@deltafacilitiesservices.com' },
  { name: 'Central', office: 'Toronto Office', email: 'info@deltafacilitiesservices.com' },
  { name: 'Quebec', office: 'Montreal Office', email: 'info@deltafacilitiesservices.com' },
  { name: 'Atlantic', office: 'Halifax Office', email: 'info@deltafacilitiesservices.com' },
]

function Contact() {
  const [submitted, setSubmitted] = useState(false)
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitted(true)
    setTimeout(() => {
      setSubmitted(false)
      setForm({ name: '', email: '', subject: '', message: '' })
    }, 4000)
  }

  return (
    <div>
      {/* HERO */}
      <section className="bg-grey-soft py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-orange-accent" />
              <span className="text-xs uppercase tracking-[0.3em] text-orange-accent font-semibold">Contact</span>
            </div>
            <h1 className="font-display text-5xl md:text-6xl font-bold text-navy leading-[1.05]">
              Let's talk about <span className="text-orange-accent">your property.</span>
            </h1>
            <p className="mt-6 text-lg text-muted leading-relaxed">
              Reach a local Delta team directly — or use the form below and we'll route your inquiry to the right specialist.
            </p>
          </div>
        </div>
      </section>

      {/* CONTACT METHODS */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { icon: Mail, title: 'Email', detail: 'info@deltafacilitiesservices.com', note: 'Replies within 1 business hour' },
            { icon: MessageCircle, title: 'Live chat', detail: 'Available now', note: '7am–10pm ET, 7 days a week' },
          ].map((c) => {
            const Icon = c.icon
            return (
              <div key={c.title} className="bg-white border border-soft rounded-2xl p-8 hover-lift">
                <div className="w-12 h-12 rounded-lg bg-orange-accent/10 flex items-center justify-center mb-5">
                  <Icon className="w-5 h-5 text-orange-accent" />
                </div>
                <div className="text-xs uppercase tracking-wider text-muted font-semibold">{c.title}</div>
                <div className="font-display text-xl font-bold text-navy mt-1.5">{c.detail}</div>
                <div className="text-sm text-muted mt-2">{c.note}</div>
              </div>
            )
          })}
        </div>
      </section>

      {/* MAIN CONTACT SECTION */}
      <section className="py-24 bg-grey-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Form */}
          <div>
            <SectionHeading
              eyebrow="Send a message"
              title="We read every inquiry."
            />
            {submitted ? (
              <div className="mt-8 bg-white border border-soft rounded-2xl p-10 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-orange-accent/10 flex items-center justify-center mb-5">
                  <Check className="w-6 h-6 text-orange-accent" />
                </div>
                <h3 className="font-display text-2xl text-navy font-bold mb-2">Thank you!</h3>
                <p className="text-muted">We'll be in touch within one business hour.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-8 bg-white border border-soft rounded-2xl p-8 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input required name="name" value={form.name} onChange={handleChange} placeholder="Full name"
                    className="w-full px-4 py-3 border border-soft rounded-lg text-sm focus:outline-none focus:border-orange-accent transition" />
                  <input required type="email" name="email" value={form.email} onChange={handleChange} placeholder="Email"
                    className="w-full px-4 py-3 border border-soft rounded-lg text-sm focus:outline-none focus:border-orange-accent transition" />
                </div>
                <input required name="subject" value={form.subject} onChange={handleChange} placeholder="Subject"
                  className="w-full px-4 py-3 border border-soft rounded-lg text-sm focus:outline-none focus:border-orange-accent transition" />
                <textarea required name="message" value={form.message} onChange={handleChange} rows={6} placeholder="How can we help?"
                  className="w-full px-4 py-3 border border-soft rounded-lg text-sm focus:outline-none focus:border-orange-accent transition resize-none" />
                <button type="submit"
                  className="w-full bg-orange-accent hover:bg-orange-dark text-white py-3.5 rounded-lg font-semibold text-sm tracking-wide transition-all flex items-center justify-center gap-2 group">
                  Send Message <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            )}
          </div>

          {/* Regional offices */}
          <div>
            <SectionHeading
              eyebrow="Regional Offices"
              title="Reach a local team directly."
            />
            <div className="mt-8 space-y-4">
              {regions.map((r) => (
                <div key={r.name} className="bg-white border border-soft rounded-xl p-6 hover-lift">
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="w-3.5 h-3.5 text-orange-accent" />
                        <span className="text-xs uppercase tracking-wider text-orange-accent font-semibold">{r.name}</span>
                      </div>
                      <h4 className="font-display text-lg font-bold text-navy">{r.office}</h4>
                    </div>
                    <Clock className="w-4 h-4 text-muted mt-1" />
                  </div>
                  <div className="mt-3 flex flex-col sm:flex-row sm:gap-6 gap-1.5 text-sm">
                    <a href={`mailto:${r.email}`} className="flex items-center gap-2 text-navy hover:text-orange-accent transition-colors">
                      <Mail className="w-3.5 h-3.5" /> {r.email}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COVERAGE MAP */}
      <section className="py-24 bg-navy text-white relative overflow-hidden">
        <img
          src="https://static.lumi.new/7a/7aecc72fc43949081279abfe8b66fa93.png"
          alt="Canadian service coverage"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight">
            Nationwide coverage. <span className="text-orange-accent">Local teams.</span>
          </h2>
          <p className="mt-5 text-white/75 text-lg">
            Serving every major Canadian market with the same standard of excellence.
          </p>
        </div>
      </section>
    </div>
  )
}

export default Contact
